import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useB2BTranslation } from "../B2BShared/hooks/useB2BTranslation";
import "./B2BPrivacyPage.css";

const B2BPrivacyPage = () => {
  const { t } = useB2BTranslation();

  return (
    <div className="b2b-privacy-page">
      <div className="privacy-header">
        <Link
          to="/gcb"
          className="back-button"
        >
          <ArrowLeft size={20} />
          {t("contact.backToB2B")}
        </Link>
        <h1>GCB Privacy Policy</h1>
        <p className="last-updated">Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
      </div>

      <div className="privacy-container">
        <div className="privacy-content">
          <section className="privacy-section">
            <h2>1. Introduction</h2>
            <p>
              Welcome to Global Chic Business (GCB) platform. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our GCB platform, register for membership, use our services, or otherwise interact with us. We are committed to protecting your privacy and ensuring the security of your personal information.
            </p>
            <p>
              By using the Global Chic Business platform, you consent to the data practices described in this Privacy Policy. If you do not agree with the practices described in this policy, please do not use our services.
            </p>
          </section>

          <section className="privacy-section">
            <h2>2. Information We Collect</h2>
            <p>We collect several types of information from and about users of our GCB platform:</p>
            
            <h3>2.1 Personal Information</h3>
            <p>When you register for a GCB account, we may collect:</p>
            <ul>
              <li>Name, email address, and phone number</li>
              <li>Business information (company name, website, business category)</li>
              <li>Professional information (position, industry, countries of operation)</li>
              <li>Profile information (bio, products/services, social media links)</li>
              <li>Payment information (processed securely through third-party payment processors)</li>
            </ul>

            <h3>2.2 Usage Information</h3>
            <p>We automatically collect information about how you interact with our platform:</p>
            <ul>
              <li>IP address and device information</li>
              <li>Browser type and version</li>
              <li>Pages visited and time spent on pages</li>
              <li>Search queries and interactions with features</li>
              <li>Date and time of access</li>
            </ul>

            <h3>2.3 Cookies and Tracking Technologies</h3>
            <p>We use cookies, web beacons, and similar tracking technologies to:</p>
            <ul>
              <li>Remember your preferences and settings</li>
              <li>Analyze platform usage and performance</li>
              <li>Provide personalized content and advertisements</li>
              <li>Improve security and prevent fraud</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>3. How We Use Your Information</h2>
            <p>We use the information we collect for the following purposes:</p>
            <ul>
              <li><strong>Account Management:</strong> To create and manage your GCB account, verify your identity, and process your registration</li>
              <li><strong>Service Delivery:</strong> To provide access to networking features, events, and business services</li>
              <li><strong>Communication:</strong> To send you important updates, notifications, and respond to your inquiries</li>
              <li><strong>Business Networking:</strong> To facilitate connections between members and enable business opportunities</li>
              <li><strong>Platform Improvement:</strong> To analyze usage patterns, improve our services, and develop new features</li>
              <li><strong>Security:</strong> To detect and prevent fraud, abuse, and unauthorized access</li>
              <li><strong>Legal Compliance:</strong> To comply with applicable laws, regulations, and legal processes</li>
              <li><strong>Marketing:</strong> To send you promotional materials (with your consent) about our services and events</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>4. Information Sharing and Disclosure</h2>
            <p>We may share your information in the following circumstances:</p>
            
            <h3>4.1 With Other Members</h3>
            <p>Your profile information (name, company, business category, etc.) may be visible to other registered GCB members to facilitate networking and business connections.</p>

            <h3>4.2 Service Providers</h3>
            <p>We may share information with third-party service providers who perform services on our behalf, such as:</p>
            <ul>
              <li>Payment processors</li>
              <li>Email service providers</li>
              <li>Cloud hosting services</li>
              <li>Analytics providers</li>
              <li>Customer support services</li>
            </ul>

            <h3>4.3 Legal Requirements</h3>
            <p>We may disclose your information if required by law or in response to valid legal requests, such as:</p>
            <ul>
              <li>Court orders or subpoenas</li>
              <li>Government investigations</li>
              <li>Protection of rights and safety</li>
              <li>Enforcement of our Terms and Conditions</li>
            </ul>

            <h3>4.4 Business Transfers</h3>
            <p>In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity.</p>

            <h3>4.5 With Your Consent</h3>
            <p>We may share your information with third parties when you have given us explicit consent to do so.</p>
          </section>

          <section className="privacy-section">
            <h2>5. Data Security</h2>
            <p>
              We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
            </p>
            <ul>
              <li>Encryption of data in transit and at rest</li>
              <li>Secure authentication and access controls</li>
              <li>Regular security assessments and updates</li>
              <li>Employee training on data protection</li>
              <li>Incident response procedures</li>
            </ul>
            <p>
              However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your information, we cannot guarantee absolute security.
            </p>
          </section>

          <section className="privacy-section">
            <h2>6. Your Rights and Choices</h2>
            <p>Depending on your location, you may have the following rights regarding your personal information:</p>
            <ul>
              <li><strong>Access:</strong> Request access to the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal requirements)</li>
              <li><strong>Portability:</strong> Request transfer of your data to another service provider</li>
              <li><strong>Objection:</strong> Object to processing of your information for certain purposes</li>
              <li><strong>Restriction:</strong> Request restriction of processing in certain circumstances</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent for processing based on consent</li>
            </ul>
            <p>
              To exercise these rights, please contact us at <a href="mailto:info@globalchic.club">info@globalchic.club</a>. We will respond to your request within a reasonable timeframe and in accordance with applicable law.
            </p>
          </section>

          <section className="privacy-section">
            <h2>7. Data Retention</h2>
            <p>
              We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. Factors we consider when determining retention periods include:
            </p>
            <ul>
              <li>The nature and sensitivity of the information</li>
              <li>The purposes for which we process the information</li>
              <li>Legal and regulatory requirements</li>
              <li>The potential risk of harm from unauthorized use or disclosure</li>
            </ul>
            <p>
              When your information is no longer needed, we will securely delete or anonymize it in accordance with our data retention policies.
            </p>
          </section>

          <section className="privacy-section">
            <h2>8. International Data Transfers</h2>
            <p>
              Your personal information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that differ from those in your country. When we transfer your information internationally, we take appropriate safeguards to ensure your information is protected, including:
            </p>
            <ul>
              <li>Using standard contractual clauses approved by data protection authorities</li>
              <li>Ensuring the recipient country has adequate data protection laws</li>
              <li>Implementing additional security measures as necessary</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>9. Children's Privacy</h2>
            <p>
              Our GCB platform is not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected information from a child under 18, we will take steps to delete such information promptly.
            </p>
          </section>

          <section className="privacy-section">
            <h2>10. Third-Party Links</h2>
            <p>
              Our platform may contain links to third-party websites or services. We are not responsible for the privacy practices or content of these third-party sites. We encourage you to review the privacy policies of any third-party sites you visit.
            </p>
          </section>

          <section className="privacy-section">
            <h2>11. Cookies and Tracking Technologies</h2>
            <p>
              We use cookies and similar tracking technologies to enhance your experience on our platform. You can control cookies through your browser settings. However, disabling cookies may limit your ability to use certain features of our platform.
            </p>
            <p>Types of cookies we use:</p>
            <ul>
              <li><strong>Essential Cookies:</strong> Required for the platform to function properly</li>
              <li><strong>Performance Cookies:</strong> Help us understand how visitors use our platform</li>
              <li><strong>Functionality Cookies:</strong> Remember your preferences and settings</li>
              <li><strong>Advertising Cookies:</strong> Used to deliver relevant advertisements (with your consent)</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>12. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or for other reasons. We will notify you of any material changes by:
            </p>
            <ul>
              <li>Posting the updated policy on this page with a new "Last Updated" date</li>
              <li>Sending an email notification to registered users</li>
              <li>Displaying a notice on our platform</li>
            </ul>
            <p>
              Your continued use of our platform after such changes constitutes acceptance of the updated Privacy Policy. We encourage you to review this policy periodically.
            </p>
          </section>

          <section className="privacy-section">
            <h2>13. Contact Us</h2>
            <p>
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
            </p>
            <p className="contact-info">
              <strong>Email:</strong> <a href="mailto:info@globalchic.club">info@globalchic.club</a>
            </p>
            <p>
              We will make every effort to address your concerns and respond to your requests in a timely manner.
            </p>
          </section>

          <section className="privacy-section">
            <h2>14. GDPR Compliance (For EU Users)</h2>
            <p>
              If you are located in the European Economic Area (EEA), you have additional rights under the General Data Protection Regulation (GDPR), including:
            </p>
            <ul>
              <li>The right to be informed about data processing</li>
              <li>The right of access to your personal data</li>
              <li>The right to rectification of inaccurate data</li>
              <li>The right to erasure ("right to be forgotten")</li>
              <li>The right to restrict processing</li>
              <li>The right to data portability</li>
              <li>The right to object to processing</li>
              <li>Rights related to automated decision-making and profiling</li>
            </ul>
            <p>
              If you believe we have not adequately addressed your privacy concerns, you have the right to lodge a complaint with your local data protection authority.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default B2BPrivacyPage;

